"use client"
import Button from "@src/components/Button"
import ModalAddEditContact from "@src/components/DefuseSDK/components/Modal/ModalAddEditContact"
import { Dialog } from "@src/components/Dialog"
import ErrorMessage from "@src/components/ErrorMessage"
import { LIST_TOKENS } from "@src/constants/tokens"
import { useIntentsUserId } from "@src/hooks/useHoldingsAwareInitialToken"
import { useTokenList } from "@src/hooks/useTokenList"
import { FocusIcon, ScanIcon } from "@src/icons"
import { usePrivateModeStore } from "@src/stores/usePrivateModeStore"
import { useQueryClient } from "@tanstack/react-query"
import { useRouter, useSearchParams } from "next/navigation"
import { useCallback, useRef, useState } from "react"
import { formatUnits } from "viem"
import {
  useQrScanner,
  useQrScannerPermissionGate,
} from "../../hooks/useQrScanner"
import type { SupportedChainName, TokenInfo } from "../../types/base"
import { parseUnits } from "../../utils/parse"
import { formatTokenUrlSymbol } from "../../utils/tokenUrlSymbol"
import { getDecimalsForChain } from "../../utils/tokenUtils"
import { PayFromModal, type PayFromMode } from "./PayFromModal"
import { PostScanActionSheet } from "./PostScanActionSheet"
import {
  type FundingCoverage,
  preCheckFunding,
  preCheckFundingPresence,
} from "./preCheckFunding"
import { type ScanResolution, resolveScan } from "./resolveScan"

type ResolvedPayment = {
  recipient: string
  chain: SupportedChainName
  token: TokenInfo
  amountDecimal: string | null
}

type OpenState =
  | { kind: "closed" }
  | { kind: "scanner" }
  | { kind: "payFrom"; payment: ResolvedPayment; coverage: FundingCoverage }
  | {
      kind: "askAction"
      resolution: ScanResolution
      sendPending: boolean
      sendError: string | null
    }
  | { kind: "addContact"; address: string; chain: SupportedChainName }

type CoverageResolution =
  | { kind: "error"; error: string }
  | { kind: "single"; mode: PayFromMode }
  | { kind: "choose" }

function tokenForMode(
  coverage: FundingCoverage,
  mode: PayFromMode
): TokenInfo | undefined {
  const status = coverage[mode]
  return status.kind === "qualifies" ? status.token : undefined
}

function resolveFundingCoverage(coverage: FundingCoverage): CoverageResolution {
  const candidates: PayFromMode[] = []
  if (coverage.main.kind === "qualifies") candidates.push("main")
  if (coverage.confidential.kind === "qualifies")
    candidates.push("confidential")

  if (candidates.length === 0) {
    const hasUnknown =
      coverage.main.kind === "unknown" ||
      coverage.confidential.kind === "unknown"

    return {
      kind: "error",
      error: hasUnknown
        ? "Unable to check your balances. Try again."
        : "You do not have assets to fund this payment",
    }
  }

  if (candidates.length === 1) {
    return { kind: "single", mode: candidates[0] }
  }

  return { kind: "choose" }
}

export function QrPayScannerButton() {
  const userId = useIntentsUserId()
  const tokenList = useTokenList(LIST_TOKENS, false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const isPrivateModeEnabled = usePrivateModeStore(
    (state) => state.isPrivateModeEnabled
  )
  const setPrivateModeEnabled = usePrivateModeStore(
    (state) => state.setPrivateModeEnabled
  )
  const [open, setOpen] = useState<OpenState>({ kind: "closed" })
  const scanGenRef = useRef(0)
  const lastAskActionRef = useRef<Extract<
    OpenState,
    { kind: "askAction" }
  > | null>(null)
  const requestScannerPermission = useQrScannerPermissionGate()

  const closeAll = useCallback(() => {
    scanGenRef.current += 1
    setOpen({ kind: "closed" })
  }, [])

  const routeAndClose = useCallback(
    (
      payment: ResolvedPayment,
      mode: PayFromMode | undefined,
      paymentToken: TokenInfo | undefined
    ) => {
      const params = new URLSearchParams()
      params.set("token", formatTokenUrlSymbol(payment.token))
      params.set("network", payment.chain)
      params.set("recipient", payment.recipient)
      if (payment.amountDecimal) params.set("amount", payment.amountDecimal)
      if (paymentToken)
        params.set("paymentToken", formatTokenUrlSymbol(paymentToken))
      params.set("qrScan", String(Date.now()))
      setOpen({ kind: "closed" })
      if (mode != null) {
        setPrivateModeEnabled(mode === "confidential")
      }

      const path = mode === "confidential" ? "/send/confidential" : "/send"
      router.push(`${path}?${params.toString()}`)
    },
    [router, setPrivateModeEnabled]
  )

  const applyFundingCoverage = useCallback(
    (
      coverage: FundingCoverage,
      payment: ResolvedPayment
    ): { accepted: boolean; error?: string } => {
      const resolution = resolveFundingCoverage(coverage)

      if (resolution.kind === "error") {
        return { accepted: false, error: resolution.error }
      }

      if (resolution.kind === "single") {
        routeAndClose(
          payment,
          resolution.mode,
          tokenForMode(coverage, resolution.mode)
        )
      } else {
        setOpen({ kind: "payFrom", payment, coverage })
      }
      return { accepted: true }
    },
    [routeAndClose]
  )

  const { videoRef, error: scannerError } = useQrScanner({
    enabled: open.kind === "scanner",
    onScan: async (rawText) => {
      const currentTokenSymbol = searchParams.get("token")
      const result = resolveScan({
        rawText,
        tokenList,
        currentTokenSymbol,
      })

      if (result.kind === "reject") {
        return { accepted: false, error: result.message }
      }

      if (result.kind === "pay") {
        const decimals = getDecimalsForChain(result.token, result.chain)
        const amountDecimal =
          result.amount.kind === "decimal"
            ? result.amount.value
            : formatUnits(BigInt(result.amount.value), decimals)

        const resolvedPayment: ResolvedPayment = {
          recipient: result.recipient,
          chain: result.chain,
          token: result.token,
          amountDecimal,
        }
        if (userId == null) {
          routeAndClose(resolvedPayment, undefined, undefined)
          return { accepted: true }
        }
        const requestedAmount = {
          amount: parseUnits(amountDecimal, decimals),
          decimals,
        }
        const capturedGen = scanGenRef.current
        const coverage = await preCheckFunding({
          destinationToken: result.token,
          requestedAmount,
          tokenList,
          userId,
          fetchQuery: queryClient.fetchQuery.bind(queryClient),
        })
        if (capturedGen !== scanGenRef.current) return { accepted: true }
        return applyFundingCoverage(coverage, resolvedPayment)
      }

      // result.kind === "askAction"
      scanGenRef.current += 1
      setOpen({
        kind: "askAction",
        resolution: result.resolution,
        sendPending: false,
        sendError: null,
      })
      return { accepted: true }
    },
  })

  const handleAskActionSend = useCallback(async () => {
    if (open.kind !== "askAction") return
    const { resolution } = open

    // The sheet only renders a Send row when the scan resolved a token, so
    // `handleAskActionSend` is unreachable without one — guard regardless.
    if (!resolution.token) return

    if (userId == null) {
      routeAndClose(
        {
          recipient: resolution.address,
          chain: resolution.chain,
          token: resolution.token,
          amountDecimal: null,
        },
        undefined,
        undefined
      )
      return
    }

    setOpen({ ...open, sendPending: true, sendError: null })
    const capturedGen = scanGenRef.current
    const coverage = await preCheckFundingPresence({
      tokenList,
      userId,
      fetchQuery: queryClient.fetchQuery.bind(queryClient),
    })
    if (capturedGen !== scanGenRef.current) return

    const resolvedPayment: ResolvedPayment = {
      recipient: resolution.address,
      chain: resolution.chain,
      token: resolution.token,
      amountDecimal: null,
    }
    const result = applyFundingCoverage(coverage, resolvedPayment)
    if (!result.accepted) {
      setOpen({ ...open, sendPending: false, sendError: result.error ?? null })
    }
  }, [
    open,
    userId,
    tokenList,
    queryClient,
    routeAndClose,
    applyFundingCoverage,
  ])

  const handleAskActionSave = useCallback(() => {
    if (open.kind !== "askAction") return
    scanGenRef.current += 1
    setOpen({
      kind: "addContact",
      address: open.resolution.address,
      chain: open.resolution.chain,
    })
  }, [open])

  const handleContactSaved = useCallback(() => {
    setOpen({ kind: "closed" })
    router.push("/contacts")
  }, [router])

  if (open.kind === "askAction") {
    lastAskActionRef.current = open
  }
  const askAction = open.kind === "askAction" ? open : lastAskActionRef.current

  return (
    <>
      <Button
        variant="secondary"
        onClick={async () => {
          if (!(await requestScannerPermission())) return
          scanGenRef.current += 1
          setOpen({ kind: "scanner" })
        }}
        className="size-10!"
        aria-label="Scan payment QR"
      >
        <ScanIcon className="size-5" />
      </Button>

      <Dialog
        title="Scan QR code"
        open={open.kind === "scanner"}
        onClose={closeAll}
      >
        <div className="space-y-4">
          <div className="relative aspect-[0.8] overflow-hidden rounded-2xl border-2 border-gray-900 bg-gray-900 dark:border-white/10 dark:bg-gray-700">
            <video
              ref={videoRef}
              className="absolute inset-0 size-full object-cover"
              autoPlay
              muted
              playsInline
            />
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center px-5">
              <FocusIcon className="aspect-square w-full text-white" />
              <span className="text-balance text-center font-semibold text-base/5 text-white">
                Scan a payment QR code.
              </span>
            </div>
          </div>

          {scannerError && (
            <ErrorMessage className="text-balance text-center">
              {scannerError}
            </ErrorMessage>
          )}

          <Button size="xl" variant="secondary" fullWidth onClick={closeAll}>
            Close
          </Button>
        </div>
      </Dialog>

      <PayFromModal
        open={open.kind === "payFrom"}
        currentMode={isPrivateModeEnabled ? "confidential" : "main"}
        onSelect={(mode) => {
          if (open.kind === "payFrom") {
            routeAndClose(open.payment, mode, tokenForMode(open.coverage, mode))
          }
        }}
        onCancel={closeAll}
      />

      <PostScanActionSheet
        open={open.kind === "askAction"}
        address={askAction?.resolution.address ?? ""}
        chain={askAction?.resolution.chain ?? "eth"}
        sendToken={askAction?.resolution.token ?? null}
        isSendPending={askAction?.sendPending ?? false}
        sendError={askAction?.sendError ?? null}
        onSend={handleAskActionSend}
        onSaveContact={handleAskActionSave}
        onCancel={closeAll}
      />

      <ModalAddEditContact
        open={open.kind === "addContact"}
        defaultValues={
          open.kind === "addContact"
            ? { address: open.address, blockchain: open.chain }
            : undefined
        }
        onClose={closeAll}
        onSuccess={handleContactSaved}
      />
    </>
  )
}
